import { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { Skeleton, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const ProductDialog = ({ sku, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (sku) api.get(`/tools/price-tracker/product/${encodeURIComponent(sku)}`).then(({ data }) => setD(data));
  }, [sku]);

  return (
    <Dialog open={!!sku} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="product-dialog">
        {!d ? <Skeleton className="h-64" /> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-start gap-3 text-base">
                <SkuThumb sku={d.sku} alt={d.title} />
                <span>
                  {d.title || d.sku}
                  <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                    SKU {d.sku} · ASIN {d.asin || "—"} {d.category && <Badge className="ml-1 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 border-0">{d.category}</Badge>}
                  </span>
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="product-stocks">
              {[["FBA sellable", d.stocks.fulfillable], ["Inbound", d.stocks.inbound], ["Reserved", d.stocks.reserved],
                ["Amazon FCs", d.stocks.amazon_fc], ["WareIQ", d.stocks.wareiq], ["Our WH", d.stocks.own_warehouse]].map(([l, v]) => (
                <div key={l} className="bg-muted/50 rounded-md p-2 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
                  <p className="font-heading font-bold">{v}</p>
                </div>
              ))}
            </div>

            {d.daily.length > 1 && (
              <div data-testid="product-price-history">
                <h4 className="text-sm font-semibold mb-2">Price history — daily ASP (last 60 days)</h4>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={d.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 9 }} domain={["auto", "auto"]} tickFormatter={(v) => `₹${v}`} width={55} />
                    <Tooltip formatter={(v, n) => n === "asp" ? [inr(v), "ASP"] : [v, "Units"]}
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Line type="monotone" dataKey="asp" stroke="#2563EB" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div data-testid="product-monthly">
                <h4 className="text-sm font-semibold mb-2">Monthly ASP & sales</h4>
                <table className="w-full text-xs">
                  <thead className="bg-muted/60 text-muted-foreground uppercase tracking-wider">
                    <tr>{["Month", "Units", "Revenue", "ASP"].map((h) => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {[...d.monthly].reverse().map((m) => (
                      <tr key={m.month} className="border-t border-border">
                        <td className="px-2 py-1.5 font-medium">{m.month}</td>
                        <td className="px-2 py-1.5">{m.units}</td>
                        <td className="px-2 py-1.5">{inr(m.revenue)}</td>
                        <td className="px-2 py-1.5 font-semibold">{m.asp != null ? inr(m.asp) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div data-testid="product-settlement-breakup">
                <h4 className="text-sm font-semibold mb-2">Settlement breakup (per month)</h4>
                {d.settlement_breakup.length === 0 && <p className="text-xs text-muted-foreground">No settlement rows yet for this SKU.</p>}
                {d.settlement_breakup.map((b) => (
                  <div key={b.month} className="mb-3 border border-border rounded-md overflow-hidden">
                    <div className="px-2 py-1.5 bg-muted/60 flex justify-between text-xs font-semibold">
                      <span>{b.month}</span>
                      <span>Net {inr(b.net)}{b.net_per_unit != null && ` · ${inr(b.net_per_unit)}/unit`}</span>
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {b.lines.map((l, i) => (
                          <tr key={i} className="border-t border-border">
                            <td className="px-2 py-1">{l.label}</td>
                            <td className={`px-2 py-1 text-right tabular-nums ${l.amount < 0 ? "text-red-600" : "text-green-600"}`}>{inr(l.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const PriceTracker = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [openSku, setOpenSku] = useState(null);

  useEffect(() => {
    let timer;
    let alive = true;
    api.get(`/tools/price-tracker?page=${page}&search=${encodeURIComponent(q)}`).then(({ data }) => {
      if (!alive) return;
      setData(data);
      if (data.thumbnails_pending) timer = setTimeout(() =>
        api.get(`/tools/price-tracker?page=${page}&search=${encodeURIComponent(q)}`).then(({ data: d2 }) => alive && setData(d2)), 20000);
    });
    return () => { alive = false; clearTimeout(timer); };
  }, [page, q]);

  if (!data) return <Skeleton className="h-96" />;

  const Delta = ({ cur, prev }) => {
    if (cur == null || prev == null || !prev) return null;
    const pct = ((cur - prev) / prev) * 100;
    if (Math.abs(pct) < 0.5) return null;
    return <span className={`text-[10px] ml-1 font-semibold ${pct > 0 ? "text-green-600" : "text-red-600"}`}>{pct > 0 ? "▲" : "▼"}{Math.abs(pct).toFixed(1)}%</span>;
  };

  return (
    <div className="space-y-4" data-testid="price-tracker">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          Price history — ASP, per-unit settlement & units for the past 3 months plus <strong>{data.cur_month}</strong> (month-to-date). Sorted by highest-selling first. Click a SKU for full details.
        </p>
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setQ(search); }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product or SKU… (enter)"
            data-testid="pt-search-input"
            className="w-72 h-9 rounded-full border border-border bg-card px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        </form>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="pt-table">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3 font-semibold" colSpan={2}>Product</th>
              <th className="text-left px-4 py-3 font-semibold">SKU</th>
              {data.months.map((m) => (
                <th key={m} className="text-left px-4 py-3 font-semibold">
                  {m}{m === data.cur_month && <span className="ml-1 normal-case font-normal text-[10px]">(MTD)</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`pt-row-${r.sku}`}>
                <td className="pl-4 py-2 w-12"><SkuThumb sku={r.sku} alt={r.title} /></td>
                <td className="px-3 py-2 font-medium max-w-[16rem] truncate">{r.title || r.sku}</td>
                <td className="px-4 py-2">
                  <button onClick={() => setOpenSku(r.sku)} data-testid={`pt-sku-${r.sku}`}
                    className="font-mono text-xs text-blue-600 hover:underline">{r.sku}</button>
                </td>
                {r.history.map((h, i) => (
                  <td key={h.month} className="px-4 py-2 align-top" data-testid={`pt-cell-${r.sku}-${h.month}`}>
                    {h.units > 0 || h.settle_pu != null ? (
                      <>
                        <span className="font-semibold">{h.asp != null ? inr(h.asp) : "—"}</span>
                        {i > 0 && <Delta cur={h.asp} prev={r.history[i - 1].asp} />}
                        <span className="block text-[11px] text-muted-foreground mt-0.5">
                          {h.settle_pu != null ? <>settle {inr(h.settle_pu)}</> : "settle —"} · {h.units.toLocaleString("en-IN")} u
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground" data-testid="pt-pagination">
          <span>{data.total.toLocaleString("en-IN")} products · page {data.page} of {data.pages}</span>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-full h-8" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pt-prev-page">
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" /> Prev
            </Button>
            <Button size="sm" variant="outline" className="rounded-full h-8" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="pt-next-page">
              Next <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>
      <ProductDialog sku={openSku} onClose={() => setOpenSku(null)} />
    </div>
  );
};
